<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { campusName } from '@/data/campuses';
import { categoryByKey } from '@/data/categories';
import { searchLocations, type SearchIndexEntry } from '@/data/search';
import type { MapLocation } from '@/data/types';

const props = withDefaults(
  defineProps<{
    index: SearchIndexEntry[];
    disabled?: boolean;
    /** Must be unique on the page when more than one search box is shown. */
    inputId?: string;
    label?: string;
    placeholder?: string;
    /** Slug to leave out of results (e.g. the destination when picking a start point). */
    exclude?: string | null;
    autofocus?: boolean;
  }>(),
  {
    inputId: 'location-search',
    label: 'Search buildings and parking',
    placeholder: 'Search buildings and parking',
    exclude: null,
  },
);
const emit = defineEmits<{ select: [slug: string] }>();

const query = ref('');
const open = ref(false);
const activeIndex = ref(-1);
const input = ref<HTMLInputElement>();

const results = computed<MapLocation[]>(() =>
  searchLocations(props.index, query.value, props.exclude ? 9 : 8)
    .filter((l) => l.slug !== props.exclude)
    .slice(0, 8),
);
const listId = computed(() => `${props.inputId}-results`);

onMounted(() => {
  if (props.autofocus) input.value?.focus();
});
const showList = computed(() => open.value && query.value.trim().length > 0);

watch(results, () => {
  activeIndex.value = results.value.length ? 0 : -1;
});

function choose(location: MapLocation | undefined) {
  if (!location) return;
  emit('select', location.slug);
  query.value = '';
  open.value = false;
  input.value?.blur();
}

function onKeydown(e: KeyboardEvent) {
  const n = results.value.length;
  switch (e.key) {
    case 'ArrowDown':
      if (!n) return;
      e.preventDefault();
      open.value = true;
      activeIndex.value = (activeIndex.value + 1) % n;
      break;
    case 'ArrowUp':
      if (!n) return;
      e.preventDefault();
      activeIndex.value = (activeIndex.value - 1 + n) % n;
      break;
    case 'Enter':
      if (showList.value) {
        e.preventDefault();
        choose(results.value[activeIndex.value]);
      }
      break;
    case 'Escape':
      if (query.value) {
        e.preventDefault();
        query.value = '';
      }
      open.value = false;
      break;
  }
}

const optionId = (i: number) => `${props.inputId}-option-${i}`;
</script>

<template>
  <div class="search">
    <label :for="inputId" class="visually-hidden">{{ label }}</label>
    <span class="mask-icon search__icon" aria-hidden="true" />
    <input
      :id="inputId"
      ref="input"
      v-model="query"
      class="search__input"
      type="search"
      role="combobox"
      autocomplete="off"
      spellcheck="false"
      :placeholder="placeholder"
      aria-autocomplete="list"
      :aria-controls="listId"
      :aria-expanded="showList"
      :aria-activedescendant="showList && activeIndex >= 0 ? optionId(activeIndex) : undefined"
      :disabled="disabled"
      @focus="open = true"
      @blur="open = false"
      @input="open = true"
      @keydown="onKeydown"
    />
    <ul v-show="showList" :id="listId" class="search__results" role="listbox" aria-label="Search results">
      <li
        v-for="(loc, i) in results"
        :id="optionId(i)"
        :key="loc.slug"
        role="option"
        class="search__option"
        :class="{ 'is-active': i === activeIndex }"
        :aria-selected="i === activeIndex"
        @mousedown.prevent="choose(loc)"
        @mousemove="activeIndex = i"
      >
        <span class="search__title">{{ loc.title }}</span>
        <span class="search__meta">{{ categoryByKey(loc.category)?.label ?? loc.category }} · {{ campusName(loc.campus) }}</span>
      </li>
      <li v-if="!results.length" class="search__empty" role="presentation">No matches for “{{ query.trim() }}”</li>
    </ul>
    <p class="visually-hidden" aria-live="polite">
      {{ showList ? `${results.length} result${results.length === 1 ? '' : 's'}` : '' }}
    </p>
  </div>
</template>

<style scoped>
.search {
  position: relative;
}

.search__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  width: 16px;
  height: 16px;
  translate: 0 -50%;
  color: var(--text-muted);
  --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M416 208c0 45.9-14.9 88.3-40 122.7l126.6 126.7a32 32 0 0 1-45.3 45.3L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0s208 93.1 208 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z'/%3E%3C/svg%3E");
}

.search__input {
  width: 100%;
  height: 44px;
  padding: 0 12px 0 36px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface);
  font-size: 16px; /* 16px stops iOS zooming on focus */
}

.search__input::placeholder {
  color: var(--text-muted);
}

.search__results {
  position: absolute;
  z-index: 10;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  max-height: min(60vh, 420px);
  overflow-y: auto;
}

.search__option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  cursor: pointer;
}

.search__option.is-active {
  background: var(--surface-alt);
}

.search__title {
  font-weight: 600;
}

.search__meta,
.search__empty {
  font-size: 13px;
  color: var(--text-muted);
}

.search__empty {
  padding: 8px 12px;
}
</style>
